import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  TableBody, 
  IconButton, 
  Alert, 
  Snackbar, 
  Tooltip,
  CircularProgress,
  Box
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import { 
  MainContainer, 
  HeaderContainer, 
  PageTitle, 
  CreateButton, 
  StyledTableContainer, 
  StyledTable, 
  StyledTableHead, 
  StyledTableRow, 
  StyledTableCell,
  StatusChip
} from './styles';
import { Button } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { 
  fetchSuperAdminSignupFields, 
  deleteSuperAdminSignupField
} from '../../../../modules/superadmin/actions';
import { resetSuperAdminActionState } from '../../../../modules/superadmin/reducers';
import { RootState } from '../../../../store';
import FieldModal from './components/FieldModal';
import PreviewModal from './components/PreviewModal';
import { SignupField } from '../../../../modules/superadmin/types';
import { AppDispatch } from '../../../../store';
import { useBreadcrumb } from '../../../../context/BreadcrumbContext';
import { useSidebar } from '../../../../context/SidebarContext';
import ConfirmationDialog from '../../../../components/common/ConfirmationDialog';

const PAGE_SIZE = 100;

const SignupFormBuilder: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();
  
  const { 
    signupFields, 
    signupFieldsLoading, 
    createSignupFieldSuccess, 
    updateSignupFieldSuccess, 
    deleteSignupFieldSuccess,
    actionMessage 
  } = useSelector((state: RootState) => state.superadmin);

  const [modalOpen, setModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedField, setSelectedField] = useState<SignupField | null>(null);
  const [alert, setAlert] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [confirmation, setConfirmation] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: (() => void) | null;
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
  });

  useEffect(() => {
    setActivePage('signup-form');
    setPath([
      { title: 'Master Data', path: '/super-admin/masterdata/signup-form' },
      { title: 'Organization Signup Form', path: '/super-admin/masterdata/signup-form' },
    ]);
    setIsShow(true);
    dispatch(fetchSuperAdminSignupFields({ page: 1, page_size: PAGE_SIZE }));
  }, [setActivePage, setPath, setIsShow, dispatch]);

  useEffect(() => {
    if (createSignupFieldSuccess || updateSignupFieldSuccess || deleteSignupFieldSuccess) {
      setAlert({ 
        message: actionMessage || 'Action completed successfully', 
        severity: 'success' 
      });
      setModalOpen(false);
      dispatch(fetchSuperAdminSignupFields({ page: 1, page_size: PAGE_SIZE }));
      dispatch(resetSuperAdminActionState());
    }
  }, [createSignupFieldSuccess, updateSignupFieldSuccess, deleteSignupFieldSuccess, actionMessage, dispatch]);

  const handleEdit = (field: SignupField) => {
    setSelectedField(field);
    setModalOpen(true);
  };

  const handleDelete = (id: number) => {
    setConfirmation({
      open: true,
      title: 'Confirm Deletion',
      message: 'Are you sure you want to delete this field?',
      onConfirm: () => {
        dispatch(deleteSuperAdminSignupField(id));
        setConfirmation({ open: false, title: '', message: '', onConfirm: null });
      }
    });
  };

  const handleAddNew = () => {
    setSelectedField(null);
    setModalOpen(true);
  };

  return (
    <MainContainer>
      <HeaderContainer>
        <PageTitle>Organization Signup Form Builder</PageTitle>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            startIcon={<VisibilityIcon />}
            onClick={() => setPreviewOpen(true)}
            sx={{ 
              textTransform: 'none', 
              borderColor: '#5E8E7E', 
              color: '#5E8E7E',
              fontWeight: 600,
              '&:hover': { borderColor: '#4A7A6A', backgroundColor: '#F1F8F6' }
            }}
          >
            Preview Form
          </Button>
          <CreateButton onClick={handleAddNew}>Add New Field</CreateButton>
        </Box>
      </HeaderContainer>

      <StyledTableContainer>
        <StyledTable>
          <StyledTableHead>
            <StyledTableRow>
              <StyledTableCell>Order</StyledTableCell>
              <StyledTableCell>Label</StyledTableCell>
              <StyledTableCell>Type</StyledTableCell>
              <StyledTableCell>Required</StyledTableCell>
              <StyledTableCell>Mapped Document</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell align="right">Actions</StyledTableCell>
            </StyledTableRow>
          </StyledTableHead>
          <TableBody>
            {signupFieldsLoading ? (
              <StyledTableRow>
                <StyledTableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={24} sx={{ color: '#5E8E7E' }} />
                </StyledTableCell>
              </StyledTableRow>
            ) : signupFields.length === 0 ? (
              <StyledTableRow>
                <StyledTableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  No fields configured.
                </StyledTableCell>
              </StyledTableRow>
            ) : (
              signupFields.map((field) => (
                <StyledTableRow key={field.field_id}>
                  <StyledTableCell>{field.order}</StyledTableCell>
                  <StyledTableCell sx={{ fontWeight: 600 }}>{field.label}</StyledTableCell>
                  <StyledTableCell sx={{ textTransform: 'capitalize' }}>{field.field_type.replace('_', ' ')}</StyledTableCell>
                  <StyledTableCell>
                    {field.is_required ? (
                      <Chip label="Required" size="small" sx={{ backgroundColor: '#FFF3E0', color: '#E65100', fontWeight: 600 }} />
                    ) : 'Optional'}
                  </StyledTableCell>
                  <StyledTableCell>{field.document_type_name || '-'}</StyledTableCell>
                  <StyledTableCell>
                    <StatusChip 
                      label={field.is_active ? 'Active' : 'Inactive'} 
                      active={field.is_active.toString()} 
                      size="small"
                    />
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      {field.is_constant ? (
                        <Tooltip title="System Field (Locked)">
                          <IconButton size="small" disabled>
                            <LockIcon fontSize="small" sx={{ color: '#9e9e9e' }} />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleEdit(field)}>
                              <EditIcon fontSize="small" sx={{ color: '#5E8E7E' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDelete(field.field_id)}>
                              <DeleteIcon fontSize="small" sx={{ color: '#D32F2F' }} />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </StyledTableCell>
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>

      <FieldModal 
        open={modalOpen} 
        onClose={() => setModalOpen(false)} 
        field={selectedField} 
      />

      <PreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        fields={signupFields}
      />

      <ConfirmationDialog
        open={confirmation.open}
        onClose={() => setConfirmation({ open: false, title: '', message: '', onConfirm: null })}
        onConfirm={confirmation.onConfirm || (() => { })}
        title={confirmation.title}
        message={confirmation.message}
      />

      <Snackbar 
        open={!!alert} 
        autoHideDuration={6000} 
        onClose={() => setAlert(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {alert ? (
          <Alert onClose={() => setAlert(null)} severity={alert.severity} sx={{ width: '100%' }}>
            {alert.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </MainContainer>
  );
};

// Helper for Chip which was not imported in styles correctly or needs to be used here
const Chip = ({ label, size, sx }: any) => (
  <Box component="span" sx={{ 
    px: 1, 
    py: 0.5, 
    borderRadius: '4px', 
    fontSize: '0.75rem',
    ...sx 
  }}>
    {label}
  </Box>
);

export default SignupFormBuilder;
